/**
 * Demarrage de la caisse et choix de l'ecran.
 *
 * 1. Le navigateur a-t-il WebCrypto et IndexedDB ? Sinon, refus clair.
 * 2. Ouverture du stockage du poste (bucket persistant ou base par defaut),
 *    puis demande de stockage persistant (avertissement si refuse).
 * 3. Lecture de l'enrolement dans `meta` : enrolement, poste revoque, poste
 *    suspendu, ou ecran NIP. Tout vient d'IndexedDB : rien n'exige le
 *    reseau, sauf l'activation elle-meme.
 * 4. PR 1.4 : synchro du catalogue et de la liste des employes au demarrage,
 *    chaque nuit a 3 h (heure de Toronto), au retour du reseau apres un echec,
 *    quand le serveur annonce un catalogue plus recent, et sur demande.
 *    La session NIP ne vit qu'en memoire : apres un redemarrage, le NIP est
 *    redemande.
 *
 * Les dependances arrivent par `services` (voir main.jsx) pour etre
 * remplacees dans les tests.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import BlockingScreen from './BlockingScreen';
import EnrollScreen from './EnrollScreen';
import PinScreen from './PinScreen';
import RevokedScreen from './RevokedScreen';
import SessionScreen from './SessionScreen';
import SetupScreen from './SetupScreen';
import StatusBanner, { StorageWarning } from './StatusBanner';
import SuspendedScreen from './SuspendedScreen';
import UpdatePrompt from './UpdatePrompt';
import { catalogFreshness } from './catalogFreshness';
import { loadTerminalState, saveTerminalStatus } from './enrollment';
import { useNightly } from './nightly';
import { evaluateSaleAccess } from './saleGuard';
import { useCaisseSync } from './useCaisseSync';
import { useHeartbeat } from './useHeartbeat';
import { putMeta } from '../store/meta';
import { META_KEYS } from '../store/schema';
import FluxCaisse from '../paiement/FluxCaisse';
import EcranVente from '../vente/EcranVente';
import { ETAPES_CAISSE, venteEnCours } from '../vente/contrat';

const KNOWN_STATUSES = ['ACTIVE', 'DISABLED', 'REVOKED'];

// Catalogue annonce perime par le battement de coeur : une synchro au plus
// tous les quarts d'heure (le serveur accepte 20 catalogues par 15 min).
const STALE_SYNC_MIN_INTERVAL_MS = 15 * 60_000;
// Retour du reseau apres une synchro en echec : au plus une par minute (un
// reseau instable bascule souvent, chaque bascule n'a pas a tout relancer).
const RETRY_SYNC_MIN_INTERVAL_MS = 60_000;
const CLOCK_TICK_MS = 30_000;

const MISSING_LABELS = {
    webcrypto: 'le chiffrement WebCrypto',
    indexeddb: 'le stockage IndexedDB',
};

export default function App({ services }) {
    const {
        missingCapabilities, openStorage, ensurePersistence, api, serviceWorker,
        appVersion, heartbeatIntervalMs, updateCheckIntervalMs,
    } = services;

    const [boot, setBoot] = useState({ phase: 'booting' });
    const [storageProtected, setStorageProtected] = useState(null);
    const [terminalState, setTerminalState] = useState(null);
    const [heartbeatStatus, setHeartbeatStatus] = useState(null);
    const [updateReady, setUpdateReady] = useState(false);
    // Session NIP : en memoire seulement, jamais dans IndexedDB.
    const [session, setSession] = useState(null);
    const [managerMode, setManagerMode] = useState(false);
    const [justEnrolled, setJustEnrolled] = useState(false);
    const [clock, setClock] = useState(() => Date.now());
    const [panierNonVide, setPanierNonVide] = useState(false);
    // Etape du parcours de caisse (FluxCaisse), pour venteEnCours.
    const [etatCaisse, setEtatCaisse] = useState(null);

    useEffect(() => {
        const id = setInterval(() => setClock(Date.now()), CLOCK_TICK_MS);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const missing = missingCapabilities();
            if (missing.length > 0) {
                setBoot({ phase: 'unsupported', missing });
                return;
            }
            let storage;
            try {
                storage = await openStorage({ onVersionChange: () => !cancelled && setBoot({ phase: 'version-change' }) });
            } catch (_error) {
                if (!cancelled) setBoot({ phase: 'storage-error' });
                return;
            }
            const state = await loadTerminalState(storage.db);
            if (cancelled) return;
            setTerminalState(state);
            setBoot({ phase: 'ready', db: storage.db });
            const persisted = await ensurePersistence({ bucket: storage.bucket });
            if (!cancelled) setStorageProtected(persisted);
        })().catch(() => {
            if (!cancelled) setBoot({ phase: 'storage-error' });
        });
        return () => { cancelled = true; };
    }, [missingCapabilities, openStorage, ensurePersistence]);

    useEffect(() => {
        if (!serviceWorker) return undefined;
        serviceWorker.onUpdateReady(() => setUpdateReady(true));
        return undefined;
    }, [serviceWorker]);

    // Hors ligne, une nouvelle version ne peut pas arriver : on verifie au
    // retour en ligne, puis toutes les `updateCheckIntervalMs`.
    const [lastUpdateCheck, setLastUpdateCheck] = useState(0);
    const db = boot.db;
    const terminal = terminalState && terminalState.terminal;
    const status = terminalState && terminalState.status;
    const token = terminalState && terminalState.deviceToken;
    const active = Boolean(boot.phase === 'ready' && terminal && token && status !== 'REVOKED' && status !== 'DISABLED');

    const sync = useCaisseSync({ db, api, token, storeId: terminal ? terminal.store_id : null });
    const syncRef = useRef(sync);
    syncRef.current = sync;
    const lastAutoSync = useRef(0);

    /** Statut du poste appris en route (synchro, NIP) : garde, et session fermee s'il n'est plus actif. */
    const applyTerminalStatus = useCallback((next) => {
        if (!KNOWN_STATUSES.includes(next)) return;
        setTerminalState((current) => (current ? { ...current, status: next } : current));
        if (next !== 'ACTIVE') {
            setSession(null);
            setManagerMode(false);
        }
    }, []);

    const runSync = useCallback(async (options) => {
        lastAutoSync.current = Date.now();
        const result = await syncRef.current.runSync(options);
        if (result && result.terminalStatus) applyTerminalStatus(result.terminalStatus);
        // Liste des employes resynchronisee : l'employe connecte qui a ete
        // revoque est deconnecte ; ses droits suivent ceux du serveur.
        if (result && result.roster && result.roster.ok && result.data) {
            const { employees } = result.data;
            setSession((current) => {
                if (!current) return current;
                const record = employees.find((e) => e.employee_id === current.employee_id);
                if (!record || record.revoked) return null;
                return { ...current, rights: record.rights, display_name: record.display_name };
            });
        }
        return result;
    }, [applyTerminalStatus]);

    // Synchro au demarrage (et juste apres l'activation), une fois par ouverture.
    const startupSyncDone = useRef(false);
    useEffect(() => {
        if (!active || startupSyncDone.current) return;
        startupSyncDone.current = true;
        runSync();
    }, [active, runSync]);

    useNightly({ enabled: active, onRun: () => runSync() });

    const handleHeartbeat = useCallback(async (result) => {
        if (result.terminalStatus) setHeartbeatStatus(result.terminalStatus);
        if (KNOWN_STATUSES.includes(result.terminalStatus) && result.terminalStatus !== status && db) {
            await saveTerminalStatus(db, result.terminalStatus);
            applyTerminalStatus(result.terminalStatus);
            // Poste reactive dans l'ERP : il se debloque seul, et rattrape sa synchro.
            if (result.terminalStatus === 'ACTIVE' && status === 'DISABLED') runSync();
        }
        if (result.connection === 'online' && result.terminalStatus === 'ACTIVE' && status === 'ACTIVE') {
            const lastError = syncRef.current.catalogSync && syncRef.current.catalogSync.last_error;
            const sinceLast = Date.now() - lastAutoSync.current;
            if (result.cameOnline && lastError && sinceLast > RETRY_SYNC_MIN_INTERVAL_MS) {
                runSync();
            } else if (result.catalogStale && sinceLast > STALE_SYNC_MIN_INTERVAL_MS) {
                runSync();
            }
        }
        if (serviceWorker && result.connection === 'online'
            && (result.cameOnline || Date.now() - lastUpdateCheck > updateCheckIntervalMs)) {
            setLastUpdateCheck(Date.now());
            serviceWorker.checkForUpdate();
        }
    }, [db, status, serviceWorker, lastUpdateCheck, updateCheckIntervalMs, applyTerminalStatus, runSync]);

    const connection = useHeartbeat({
        enabled: Boolean(boot.phase === 'ready' && terminal && terminalState.deviceToken && status !== 'REVOKED'),
        api,
        token,
        appVersion,
        intervalMs: heartbeatIntervalMs,
        onResult: handleHeartbeat,
        getCatalogSnapshotId: () => (syncRef.current.catalogMeta ? syncRef.current.catalogMeta.snapshot_id : null),
    });

    const handleEnrolled = useCallback(async () => {
        setJustEnrolled(true);
        setTerminalState(await loadTerminalState(db));
    }, [db]);

    // Une session fermee ferme aussi la confirmation d'un responsable en cours.
    useEffect(() => {
        if (!session) setManagerMode(false);
    }, [session]);
    // Sans session, plus de parcours de caisse : son etape ne compte plus.
    useEffect(() => {
        if (!session) setEtatCaisse(null);
    }, [session]);

    const handleLogin = useCallback((result) => {
        setSession({ ...result.employee, mode: result.mode, started_at: new Date().toISOString() });
        syncRef.current.reload().catch(() => {});
    }, []);

    const handleManagerConfirmed = useCallback(async (result) => {
        const meta = syncRef.current.catalogMeta;
        if (meta && db) {
            await putMeta(db, {
                [META_KEYS.MANAGER_CONFIRMATION]: {
                    snapshot_id: meta.snapshot_id,
                    confirmed_at: new Date().toISOString(),
                    employee_id: result.employee.employee_id,
                    display_name: result.employee.display_name,
                },
            });
            await syncRef.current.reload();
        }
        setClock(Date.now());
        setManagerMode(false);
    }, [db]);

    const lock = useCallback(() => {
        setSession(null);
        setManagerMode(false);
    }, []);

    const freshness = catalogFreshness(sync.catalogMeta, clock, sync.managerConfirmation);
    // Jeton refuse par l'ERP (401 au battement) : vente bloquee, meme si le
    // statut garde sur le poste est encore ACTIVE.
    const effectiveStatus = heartbeatStatus === 'UNKNOWN' && status === 'ACTIVE' ? 'UNKNOWN' : status;
    const access = evaluateSaleAccess({ terminalStatus: effectiveStatus, session, freshness });
    // Paiement ou recu a l'ecran : la vente se termine meme si l'acces tombe
    // entre-temps (catalogue qui passe 72 h, poste non reconnu) ; le blocage
    // s'applique a la vente suivante.
    const paiementEnCours = Boolean(session && etatCaisse
        && (etatCaisse.etape === ETAPES_CAISSE.PAIEMENT || etatCaisse.etape === ETAPES_CAISSE.RECU));
    const parcoursCaisse = Boolean(session) && (access.allowed || paiementEnCours);

    let screen;
    if (boot.phase === 'booting') {
        screen = <Box sx={{ flex: 1 }} aria-busy="true" />;
    } else if (boot.phase === 'unsupported') {
        screen = (
            <BlockingScreen
                testId="ecran-navigateur-incompatible"
                title="Cette caisse ne peut pas démarrer sur ce navigateur"
                lines={[
                    `Il manque : ${boot.missing.map((m) => MISSING_LABELS[m] || m).join(' et ')}.`,
                    'Ouvrez caisse.amabec.ca dans Google Chrome à jour, hors navigation privée.',
                ]}
            />
        );
    } else if (boot.phase === 'storage-error') {
        screen = (
            <BlockingScreen
                testId="ecran-stockage-indisponible"
                title="Le stockage du poste est indisponible"
                lines={['Fermez complètement Chrome, puis rouvrez la caisse. Si le problème continue, appelez le responsable.']}
            />
        );
    } else if (boot.phase === 'version-change') {
        screen = (
            <BlockingScreen
                testId="ecran-autre-onglet"
                title="La caisse a été mise à jour dans un autre onglet"
                lines={['Rechargez cette page pour continuer.']}
                actionLabel="Recharger"
                onAction={() => globalThis.location.reload()}
            />
        );
    } else if (!terminal) {
        screen = <EnrollScreen db={db} api={api} onEnrolled={handleEnrolled} />;
    } else if (status === 'REVOKED') {
        screen = <RevokedScreen terminal={terminal} />;
    } else if (status === 'DISABLED') {
        screen = <SuspendedScreen terminal={terminal} />;
    } else if (justEnrolled) {
        screen = <SetupScreen terminal={terminal} sync={sync} onSync={() => runSync()} onContinue={() => setJustEnrolled(false)} />;
    } else if (!session || managerMode) {
        const confirming = Boolean(session);
        screen = (
            <PinScreen
                key={confirming ? 'responsable' : 'connexion'}
                db={db}
                api={api}
                token={token}
                connection={connection}
                terminal={terminal}
                appVersion={appVersion}
                mode={confirming ? 'manager' : 'login'}
                terminalUnknown={heartbeatStatus === 'UNKNOWN'}
                onSuccess={confirming ? handleManagerConfirmed : handleLogin}
                onCancel={() => setManagerMode(false)}
                onTerminalStatus={applyTerminalStatus}
            />
        );
    } else if (parcoursCaisse) {
        // Parcours de caisse (contrat fluxCaisse) : ouverture de la caisse si
        // aucun quart n'est ouvert, vente, paiement comptant, recu.
        screen = (
            <FluxCaisse
                db={db}
                session={session}
                EcranVente={EcranVente}
                ecranVenteProps={{
                    terminal,
                    db,
                    api,
                    token,
                    connection,
                    catalogMeta: sync.catalogMeta,
                    sync,
                    onSync: () => runSync(),
                    canSync: connection !== 'offline',
                    onLock: lock,
                    onStatutPoste: applyTerminalStatus,
                }}
                onPanierNonVide={setPanierNonVide}
                onLock={lock}
                onEtatChange={setEtatCaisse}
            />
        );
    } else {
        screen = (
            <SessionScreen
                session={session}
                access={access}
                sync={sync}
                onSync={() => runSync()}
                canSync={connection !== 'offline'}
                onLock={lock}
                onManagerConfirm={() => setManagerMode(true)}
            />
        );
    }

    return (
        <Box
            data-stockage={storageProtected === null ? 'verification' : (storageProtected ? 'protege' : 'non-protege')}
            sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
        >
            <StatusBanner
                connection={connection}
                terminal={terminal}
                freshness={terminal && status !== 'REVOKED' ? freshness : null}
                employeeName={session ? session.display_name : null}
            />
            {storageProtected === false && <StorageWarning />}
            {/* Mise a jour bloquee pendant un paiement ou avec un panier non vide. */}
            <UpdatePrompt
                ready={updateReady}
                blocked={parcoursCaisse && venteEnCours(etatCaisse, panierNonVide)}
                onReload={() => serviceWorker && serviceWorker.applyUpdate()}
            />
            {screen}
        </Box>
    );
}
